import React, { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";

// component
import TextField from "src/components/TextField";
import AuthForm from "../components/AuthForm";
import Button from "src/components/Button";

// services
import { resetPassword } from "src/services/AuthService";

import { allRoutes } from "src/constants/AllRoutes";
import { hasError } from "src/services/ApiHelpers";
import { Strings } from "src/resources";
import { rules } from "src/utils/validationPattern";

function ResetPassword() {
  const navigate = useNavigate();
  const {
    control,
    watch,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm();

  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get("continueUrl");
  const apiKey = searchParams.get("apiKey");
  const code = searchParams.get("oobCode");
  const continueUrlParams = new URL(continueUrl);
  const actionParam = continueUrlParams.searchParams.get("action");

  useEffect(() => {
    if (!code && !apiKey) {
      navigate(allRoutes.login);
    }
  }, []);

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    if (password && confirmPassword && password !== confirmPassword) {
      setError("confirmPassword", { type: "manual", message: "Passwords do not match" });
    } else {
      clearErrors("confirmPassword");
    }
  }, [password, confirmPassword]);

  const resetPasswordMutation = useMutation(resetPassword, {
    onSuccess: (data) => {
      toast.success(data?.message);
      navigate(allRoutes.login);
    },
    onError: (error) => {
      const errorResponse = hasError(error);
      toast.error(errorResponse.message);
    }
  });

  function onSubmit(data) {
    const resetPasswordData = {
      actionCode: code,
      newPassword: data?.password
    };
    resetPasswordMutation.mutate(resetPasswordData);
  }

  return (
    <>
      <AuthForm title={actionParam === "resetPassword" ? Strings.reset_password : Strings.set_password}>
        <Form>
          <Controller
            name="password"
            control={control}
            rules={rules.password(Strings.password)}
            render={({ field: { onChange, value } }) => (
              <TextField
                label={Strings.new_password}
                type="password"
                value={value}
                onChange={(newValue: string) => {
                  onChange(newValue.trim());
                }}
                placeHolder={Strings.new_password}
                dataCy="newResetPass"
              />
            )}
          />
          <Controller
            name="confirmPassword"
            control={control}
            rules={rules.password(Strings.password)}
            render={({ field: { onChange, value } }) => (
              <TextField
                label={Strings.confirm_password}
                type="password"
                value={value}
                onChange={(newValue: string) => {
                  onChange(newValue.trim());
                }}
                placeHolder={Strings.confirm_password}
                // error={error?.message}
                dataCy="confirmResetPass"
              />
            )}
          />
          <Form.Control.Feedback type="invalid">{errors?.password?.message || errors?.confirmPassword?.message}</Form.Control.Feedback>
          <div className="mt-2">
            <Button title={Strings.save_password} variant="primary" fullWidth onClick={handleSubmit(onSubmit)} dataCy="resetPasswordButton" />
          </div>
        </Form>
      </AuthForm>
    </>
  );
}

export default ResetPassword;
